import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react'
import { useLocation } from 'react-router-dom'
import { MapPin, Phone, Mail, Clock } from 'lucide-react'
import { vehicleFields, detailFields, ownerFields, valuationFields, type ValuationField } from './valuation-fields'
import { contactApiContract, contactDirectory } from '../data/contact'
import { getLocalizedPath } from '../i18n/routes'
import { useI18n } from '../i18n/I18nProvider'
import { Button } from '../ui/Button'
import { Input } from '../ui/Input'
import { Link } from '../ui/Link'
import { useRouteMetadata } from '../components/inventory/useRouteMetadata'
import { PageHero } from '../components/page-hero/PageHero'
import './contact.css'

type Errors = Record<string, string>
type ContactForm = { name: string; email: string; phone: string; vehicleReference: string; message: string; privacyAccepted: boolean }

const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/
const phonePattern = /^\+?\d{6,15}$/

function initialForm(vehicleReference: string): ContactForm {
  return { name: '', email: '', phone: '', vehicleReference, message: '', privacyAccepted: false }
}

export function InquiryForm({ valuation = false }: { valuation?: boolean }) {
  const { language, t } = useI18n()
  const vehicleReference = useMemo(() => new URLSearchParams(window.location.search).get('vehicle')?.slice(0, 160) ?? '', [])
  const [form, setForm] = useState(() => initialForm(vehicleReference))
  const [vehicle, setVehicle] = useState<Record<string, string>>({})
  const [errors, setErrors] = useState<Errors>({})
  const [attempted, setAttempted] = useState(false)
  const [status, setStatus] = useState<'idle' | 'sending' | 'success' | 'error'>('idle')
  const summaryRef = useRef<HTMLDivElement>(null)
  const resultRef = useRef<HTMLDivElement>(null)
  // Delivery is off by default: an explicit verification marker is required
  // before the browser is allowed to call the configured server endpoint.
  const isLiveDelivery = import.meta.env.VITE_CONTACT_DELIVERY === 'verified'



  useEffect(() => { if (status === 'success' || status === 'error') resultRef.current?.focus() }, [status])

  const validate = (value: ContactForm, details = vehicle): Errors => {
    const next: Errors = {}
    if (!valuation && (!value.name.trim() || value.name.trim().length > contactApiContract.existingFields.name.maxLength)) next.name = t('contact.errorName')
    if (!emailPattern.test(value.email.trim()) || value.email.trim().length > contactApiContract.existingFields.email.maxLength) next.email = t('contact.errorEmail')
    if ((valuation && !value.phone.trim()) || (value.phone && (!phonePattern.test(value.phone.trim()) || value.phone.trim().length > 32))) next.phone = t('contact.errorPhone')
    if ((!valuation && !value.message.trim()) || value.message.trim().length > (valuation ? 5000 : contactApiContract.existingFields.message.maxLength)) next.message = t('contact.errorMessage')
    if (!value.privacyAccepted) next.privacyAccepted = t('contact.errorPrivacy')
    if (valuation) {
      for (const field of valuationFields) {
        const content = (details[field.name] ?? '').trim()
        if (field.required && !content) next[field.name] = `${t(field.label)}: ${t('sell.required')}`
        else if (content && field.type === 'number' && (!Number.isInteger(Number(content)) || Number(content) < (field.min ?? 0) || Number(content) > (field.max ?? Infinity))) next[field.name] = `${t(field.label)}: ${t('sell.invalidNumber')}`
      }
    }
    return next
  }

  const update = <Key extends keyof ContactForm>(key: Key, value: ContactForm[Key]) => {
    const next = { ...form, [key]: value }
    setForm(next)
    if (attempted) setErrors(validate(next))
    if (status !== 'idle') setStatus('idle')
  }

  const onSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
    setAttempted(true)
    const nextErrors = validate(form)
    setErrors(nextErrors)
    if (Object.keys(nextErrors).length) {
      requestAnimationFrame(() => summaryRef.current?.focus())
      return
    }
    const message = valuation ? [
      t('sell.formTitle'),
      ...valuationFields.filter(field => vehicle[field.name]?.trim()).map(field => `${t(field.label)}: ${vehicle[field.name].trim()}`),
      form.message.trim(),
    ].filter(Boolean).join('\n') : form.message.trim()
    setStatus('sending')
    if (!isLiveDelivery) { setStatus('error'); return }
    try {
      const response = await fetch(contactApiContract.endpoint, {
        method: contactApiContract.method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: valuation ? `${vehicle.firstName.trim()} ${vehicle.lastName.trim()}` : form.name.trim(), email: form.email.trim(), phone: form.phone.trim() || undefined, vehicleReference: form.vehicleReference.trim() || undefined, message, privacyAccepted: form.privacyAccepted }),
      })
      if (!response.ok || (await response.json()).success !== true) throw new Error(`Contact request failed with ${response.status}`)
      setStatus('success')
    } catch { setStatus('error') }
  }

  const renderField = (field: ValuationField) => {
    const id = `valuation-${field.name}`
    const change = (value: string) => {
      const next = { ...vehicle, [field.name]: value }
      setVehicle(next)
      if (attempted) setErrors(validate(form, next))
      setStatus('idle')
    }
    return field.options ? <div className="field" key={field.name}>
      <label className="field__label" htmlFor={id}>{t(field.label)}</label>
      <select id={id} name={field.name} className="field__control" value={vehicle[field.name] ?? ''} onChange={event => change(event.target.value)}>
        <option value="">{t('sell.choose')}</option>
        {field.options.map(option => <option key={option} value={t(option)}>{t(option)}</option>)}
      </select>
    </div> : <Input key={field.name} id={id} name={field.name} label={t(field.label)} type={field.type ?? 'text'} min={field.min} max={field.max} step={field.type === 'number' ? 1 : undefined} maxLength={field.name === 'firstName' || field.name === 'lastName' ? 59 : 120} autoComplete={field.autoComplete ?? 'off'} required={field.required} value={vehicle[field.name] ?? ''} error={errors[field.name]} onChange={event => change(event.target.value)} />
  }

  return <form className="contact-form" noValidate onSubmit={onSubmit}>
      {Object.keys(errors).length ? <div className="contact-form__summary" ref={summaryRef} tabIndex={-1} role="alert"><strong>{t('contact.errorSummary')}</strong><ul>{Object.entries(errors).map(([field, error]) => <li key={field}>{error}</li>)}</ul></div> : null}
      {valuation ? <>
        <fieldset className="valuation-group"><legend>{t('sell.vehicleTitle')}</legend><div className="valuation-group__fields">{vehicleFields.map(renderField)}</div></fieldset>
        <fieldset className="valuation-group"><legend>{t('sell.detailsTitle')}</legend><p>{t('sell.optional')}</p><div className="valuation-group__fields">{detailFields.map(renderField)}</div></fieldset>
        <fieldset className="valuation-group"><legend>{t('sell.contactTitle')}</legend><div className="valuation-group__fields">{ownerFields.map(renderField)}</div></fieldset>
      </> : <Input id="contact-name" label={t('contact.nameLabel')} name="name" autoComplete="name" maxLength={contactApiContract.existingFields.name.maxLength} value={form.name} error={errors.name} onChange={(event) => update('name', event.target.value)} required />}
      <Input id="contact-email" label={t('contact.emailLabel')} name="email" type="email" autoComplete="email" maxLength={contactApiContract.existingFields.email.maxLength} value={form.email} error={errors.email} onChange={(event) => update('email', event.target.value)} required />
      <Input id="contact-phone" label={t(valuation ? 'sell.phone' : 'contact.phoneLabel')} required={valuation} hint={t('contact.phoneHint')} name="tel" type="tel" inputMode="tel" autoComplete="tel" maxLength={32} value={form.phone} error={errors.phone} onChange={(event) => update('phone', event.target.value)} />
      {!valuation && <Input id="contact-vehicle" label={t('contact.vehicleLabel')} name="vehicleReference" autoComplete="off" maxLength={160} value={form.vehicleReference} onChange={(event) => update('vehicleReference', event.target.value)} />}
      <div className="field contact-form__message"><label className="field__label" htmlFor="contact-message">{t(valuation ? 'sell.notes' : 'contact.messageLabel')}</label><textarea id="contact-message" className="field__control" name="message" autoComplete="off" maxLength={valuation ? 5000 : contactApiContract.existingFields.message.maxLength} aria-invalid={errors.message ? true : undefined} aria-describedby={errors.message ? 'contact-message-error' : undefined} value={form.message} onChange={(event) => update('message', event.target.value)} required={!valuation} />{errors.message ? <span className="field__error" id="contact-message-error">{errors.message}</span> : null}</div>
      <div className="contact-form__privacy"><input id="contact-privacy" name="privacyAccepted" type="checkbox" checked={form.privacyAccepted} onChange={(event) => update('privacyAccepted', event.target.checked)} aria-invalid={errors.privacyAccepted ? true : undefined} aria-describedby={errors.privacyAccepted ? 'contact-privacy-error' : undefined} required /><label htmlFor="contact-privacy">{t('contact.privacyLabel')}</label><Link to={getLocalizedPath('privacy', language)}>{t('contact.privacyLink')}</Link>{errors.privacyAccepted ? <span className="field__error" id="contact-privacy-error">{errors.privacyAccepted}</span> : null}</div>
      <Button type="submit" loading={status === 'sending'}>{status === 'sending' ? t('contact.sending') : t(valuation ? 'sell.submit' : 'contact.submit')}</Button>
      {status === 'success' ? <div className="contact-form__result contact-form__result--success" ref={resultRef} tabIndex={-1} role="status">{t(valuation ? 'sell.success' : 'contact.liveSuccess')}</div> : null}
      {status === 'error' ? <div className="contact-form__result contact-form__result--error" ref={resultRef} tabIndex={-1} role="alert">{t('contact.submitError')}</div> : null}
    </form>
}


export function ContactPage() {
  const { language, t } = useI18n()
  const { pathname } = useLocation()
  useRouteMetadata({ title: t('seo.contactMetadataTitle'), description: t('seo.contactMetadataDescription'), pathname, language })
  return <div className="contact-page">
    <PageHero variant="contact" eyebrow={t('hero.contactEyebrow')} title={t('contact.title')}><p>{t('contact.intro')}</p></PageHero>
    <section className="contact-form-section" aria-labelledby="contact-form-title">
      <div className="page-frame contact-layout">
        <aside className="contact-info" aria-labelledby="contact-office-title">
          <h2 id="contact-office-title">{t('contact.officeTitle')}</h2>
          <dl>
            <div><MapPin aria-hidden="true" /><dt>{t('contact.address')}</dt><dd>{contactDirectory.office.streetAddress}<br />{contactDirectory.office.postalAddress}</dd></div>
            <div><Phone aria-hidden="true" /><dt>{t('contact.phone')}</dt><dd>{contactDirectory.phone.map(phone => <a key={phone} href={`tel:+39${phone.replaceAll(' ', '')}`}>{phone}</a>)}</dd></div>
            <div><Mail aria-hidden="true" /><dt>{t('contact.emailLabel')}</dt><dd><a href={`mailto:${contactDirectory.email}`}>{contactDirectory.email}</a></dd></div>
            <div><Clock aria-hidden="true" /><dt>{t('contact.hours')}</dt><dd>{t('contact.weekdays')}<br />{t('contact.weekdayHours')}<span className="contact-info__hours">{t('contact.saturday')}<br />{t('contact.saturdayHours')}</span></dd></div>
          </dl>
        </aside>
        <div className="contact-form-panel">
          <h2 id="contact-form-title">{t('contact.formTitle')}</h2>
          <p className="contact-form-panel__intro">{t('contact.formIntro')}</p>
          <InquiryForm />
        </div>
      </div>
    </section>
    <section className="contact-map" aria-label={t('contact.mapTitle')}>
      <iframe title={t('contact.mapTitle')} src={contactDirectory.office.mapUrl} loading="lazy" referrerPolicy="no-referrer-when-downgrade" />
    </section>
  </div>
}
